import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Spin, Table, Modal, Upload, Dropdown, Menu, message, Tooltip } from 'antd'
import {
  DownOutlined,
  ExclamationCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import _ from 'lodash'

import {
  getApplicationsAction,
  applicationSelector,
  clearState,
  setSearchFilter,
  setApplication,
} from '../../../store/application'
import { userSelector } from '../../../store/session'
import { changeState, deleteApplication } from '../../../services/application'

import ApproveModal from '../ApproveModal'
import DeliveryFilesModal from '../../delivery/DeliveryFilesModal'
import ApplicationFilter from '../Filter'

import styles from './style.module.css'
import ImplementationItemsTable from '../ImplementationItemsTable'
import useLocalState from '@phntms/use-local-state'
import ColumnsSettings from '../../table/columnsSettings'
import usePagination from '../../hooks/usePagination'
import ActualAmountModal from '../ActualAmountModal'
import {
  APP_STATUS_ACTIVE,
  APP_STATUS_AGREE,
  APP_STATUS_ARCHIVE,
  APP_STATUS_DELIVERY,
  APP_STATUS_DONE,
  APP_STATUS_DRAFT,
  APP_STATUS_PAY,
  APP_STATUS_PROGRESS,
  statuses,
} from '../../reports/constants'
import { formatDate } from '../../../utils/date-format'
import { exportTableDataToXlsx } from '../../../utils/export-xls'
import { AddApplicationPaymentModal } from '../ApplicationPaymentModal'
import ApplicationPaymentsDetailModal from '../ApplicationPaymentsView/ApplicationPaymentsDetailModal'
import RefundModal from '../../RefundModal'
import { calcSumAmount } from '../../../utils/calculation'
import DeliveryDateModal from '../../delivery/DeliveryDateModal'
import { formatMoney } from '../../../utils/number-utils'

const { confirm } = Modal

const API_URL = process.env.REACT_APP_API_URL

const renderApplicationSum = (record) => {
  const itemsPrice = record?.implementation?.implementation_items
    ? record?.implementation?.implementation_items.reduce((acc, impl) => {
        const finalAmount = calcSumAmount(impl)
        if (impl.vatType === 'up' && impl.vatValue > 0 && impl.price > 0 && impl.amount > 0) {
          const sum = impl.price * finalAmount
          const vat = (sum / 100) * impl.vatValue
          return sum + vat + acc
        } else if (impl.price > 0 && impl.amount > 0) {
          return acc + impl.price * finalAmount
        }
        return acc
      }, 0)
    : 0
  const transportationPrice = record?.transportation?.reduce((acc, item) => {
    const res =
      item.vatType === 'up' && item.vatValue > 0 ? item.price + (item?.price / 100) * item.vatValue : item.price
    return acc + res
  }, 0)
  return formatMoney(itemsPrice + transportationPrice)
}

const getPaymentMethod = (value) => {
  switch (value) {
    case 'cashless':
      return 'Безналичный'
    case 'cash':
      return 'Наличный'
    case 'postponement':
      return 'Пост оплата'
    case 'postcashless':
      return 'Безналичный пред оплата'
    default:
      return 'Безналичный'
  }
}

const renderPaymentInfo = ({ implementation, payments }) => {
  const method = getPaymentMethod(implementation?.payment_type)
  const isFull = payments?.some(({ is_full }) => is_full && is_full)
  const paid = _.sum(payments?.map((p) => p.paid))
  const info = payments && payments.length > 0 && (isFull ? '(Оплачено 100%)' : paid ? `(Оплачено ${paid} руб.)` : '')
  return `${method} ${info || ''}`
}

const settingsKey = 'ApplicationTable'

const columnsDefaultSettings = [
  {
    key: 'name',
    name: 'Наименование',
    display: true,
    index: -3,
  },
  {
    key: 'amount',
    name: 'Кол-во',
    display: true,
    index: -2,
  },
  {
    key: 'actualAmount',
    name: 'Кол-во фактическое',
    display: true,
    index: -1,
  },
  {
    key: 'refund_items',
    name: 'Возврат',
    display: true,
    index: -1,
  },
  {
    key: 'correctedAmount',
    name: 'Кор-ка кол-ва',
    display: true,
    index: 1,
  },
  {
    key: 'price',
    name: 'Цена за единицу',
    display: true,
    index: 2,
  },
  {
    key: 'sum',
    name: ' Сумма',
    display: true,
    index: 3,
  },
  {
    key: 'vatType',
    name: 'Тип НДС',
    display: true,
    index: 4,
  },
  {
    key: 'vatValue',
    name: '% НДС',
    display: true,
    index: 5,
  },
  {
    key: 'vatCalc',
    name: 'НДС',
    display: true,
    index: 6,
  },
  {
    key: 'vatSum',
    name: 'Всего с НДС',
    display: true,
    index: 7,
  },
  {
    key: 'unit',
    name: 'Ед.изм.',
    display: true,
    index: 8,
  },
  {
    key: 'provider',
    name: 'Поставщик',
    display: true,
    index: 9,
  },
  {
    key: 'description',
    name: 'Комментарий',
    display: true,
    index: 10,
  },
  {
    key: 'state',
    name: 'Статус',
    display: true,
    index: 11,
  },
  {
    key: 'application_name',
    name: 'Наименование заявка',
    display: true,
    index: 12,
  },
  {
    key: 'application_amount',
    name: 'Кол-во заявка',
    display: true,
    index: 13,
  },
  {
    key: 'application_description',
    name: 'Комментарий заявка',
    display: true,
    index: 14,
  },
  {
    key: 'estimate_name',
    name: 'Наименование смета',
    display: true,
    index: 15,
  },
  {
    key: 'estimate_category',
    name: 'Категория смета',
    display: true,
    index: 16,
  },
  {
    key: 'off_budget',
    name: 'Вне сметы',
    display: true,
    index: 17,
  },
  {
    key: 'estimate_price',
    name: 'Цена смета без НДС',
    display: true,
    index: 18,
  },
  {
    key: 'estimate_unit',
    name: 'Ед.изм. смета',
    display: true,
    index: 19,
  },
  {
    key: 'estimate_amount',
    name: 'Кол-во смета',
    display: true,
    index: 20,
  },
  {
    key: 'actions',
    name: 'Действия',
    display: true,
    index: 21,
  },
]

const ApplicationsTable = ({ state = APP_STATUS_DRAFT, onStateChange, tableState }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector(userSelector)
  const [searchParams] = useSearchParams()
  const { isFetching, applications, pagination, searchFilter, meta } = useSelector(applicationSelector)
  const [isLoading, setIsLoading] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showPaymentsDetail, setShowPaymentsDetail] = useState(false)
  const [selectedApplicationId, setSelectedApplicationId] = useState(null)
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [applicationState, setApplicationState] = useState('reject')
  const [fileList, setFileList] = useState([])
  const [showFileList, setShowFileList] = useState(false)
  const [showApproval, setShowApproval] = useState(false)
  const [showDelivery, setShowDelivery] = useState(false)
  const [showActualAmount, setShowActualAmount] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false)
  const [tablePagination, page, pageSize] = usePagination(pagination?.total)
  const [filter, setFilter] = useState({
    id: null,
    name: '',
    projects: [],
    categories: [],
    dates: [],
  })
  const [expandedIds, setExpandedIds] = useState([])
  const [isTableSettingsVisible, setTableSettingsVisible] = useState(false)
  const [storedData, setStoredData] = useLocalState(settingsKey, columnsDefaultSettings)

  const isForeman = useMemo(() => user?.role?.type === 'foreman', [user])
  const isSupplier = useMemo(() => user?.role?.type === 'supplier', [user])
  const isProject = useMemo(() => user?.role?.type === 'project_manager', [user])

  useEffect(() => {
    const id = searchParams.get('id')
    if (id) {
      setFilter({ ...filter, id })
    } else if (searchFilter) {
      setFilter(searchFilter)
    }
  }, [searchParams, searchFilter])

  useEffect(() => setIsLoading(isFetching), [isFetching])

  useEffect(() => {
    if (storedData && storedData.length !== columnsDefaultSettings.length) {
      setStoredData(columnsDefaultSettings)
    }
  }, [storedData])

  useEffect(() => {
    if (
      tableState === state &&
      !isFetching &&
      (meta.state !== tableState || !_.isEqual({ page, pageSize }, meta.pagination) || !_.isEqual(filter, meta.filter))
    ) {
      getApplications()
    }
  }, [user, state, page, tableState, pageSize, filter, meta, isFetching])

  useEffect(() => {
    if (filter?.categories?.length > 0 || filter?.projects?.length > 0 || filter?.name?.length > 0) {
      setExpandedIds(applications.map((item) => item.id))
    }
  }, [applications])

  useEffect(() => {
    return () => {
      dispatch(clearState())
    }
  }, [])

  const showConfirm = (id, state) => {
    confirm({
      title: 'Вы хотите архивировать заявку?',
      icon: <ExclamationCircleOutlined />,
      content: 'После архивации заявка не будет учитываться в расчетах',
      okText: 'Архивировать',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk() {
        onStateChangePress(id, state)()
      },
    })
  }

  const onStateChangePress = (application, newState, data) => () => {
    setIsLoading(true)
    changeState(application, newState, data)
      .then(() => {
        onStateChange(tableState)
      })
      .catch(() => {
        message.error('Ошибка во время обновления состояния заявки')
      })
      .finally(() => {
        getApplications()
        setIsLoading(false)
      })
  }

  const onChangeState = async (application, newState, data) => {
    setIsLoading(true)
    changeState(application, newState, data)
      .then(() => getApplications())
      .catch(() => message.error('Ошибка во время обновления состояния заявки'))
      .finally(() => setIsLoading(false))
  }

  const onDeleteChangePress = (application) => () => {
    setIsLoading(true)
    deleteApplication(application)
      .then(() => {})
      .catch(() => {
        message.error('Ошибка во время удаления заявки')
      })
      .finally(() => {
        setIsLoading(false)
        getApplications()
      })
  }

  const onSearchFilter = useCallback((filter) => dispatch(setSearchFilter({ filter })), [dispatch])

  const onEditPress = useCallback(
    (application) => () => {
      dispatch(setApplication({ application }))
      navigate(`/application/new?project=${application.project.id}`)
    },
    [dispatch, navigate]
  )

  const onImplementationPress = useCallback(
    (application) => () => {
      dispatch(setApplication({ application }))
      navigate(`/implementation?id=${application?.implementation?.id}`)
    },
    [dispatch, navigate]
  )

  const onRefundPress = useCallback(
    (application) => () => {
      dispatch(setApplication({ application }))
      setShowRefundModal(true)
    },
    [dispatch]
  )

  const onFilesHandler = useCallback((files) => {
    setFileList(files)
    setShowFileList(true)
  }, [])

  const getApplications = () => {
    if (user && user?.projects) {
      const ids = user?.projects.map((item) => item.id)
      dispatch(
        getApplicationsAction({
          ids,
          state: tableState,
          filter,
          pagination: { page, pageSize },
        })
      )
    }
  }

  const onPaymentsDetailShowHandler = useCallback(
    (id) => () => {
      setSelectedApplicationId(id)
      setShowPaymentsDetail(true)
    },
    []
  )

  const onPaymentsDetailCloseHandler = useCallback(() => {
    setShowPaymentsDetail(false)
    setSelectedApplicationId(null)
  }, [])

  const onPaymentShowHandler = useCallback(
    (id, state) => () => {
      setSelectedApplicationId(id)
      setApplicationState(state)
      setShowPaymentModal(true)
    },
    []
  )

  const onPaymentCloseHandler = useCallback(() => {
    setShowPaymentModal(false)
    setSelectedApplicationId(null)
  }, [])

  const onPaymentApplyHandler = useCallback(
    (values) => {
      try {
        applicationState ? onChangeState(selectedApplicationId, applicationState, values) : getApplications()
      } finally {
        onPaymentCloseHandler()
      }
    },
    [selectedApplicationId, applicationState]
  )

  const onApprovalClose = (updateData = false) => {
    if (updateData) {
      getApplications()
    }
    setShowApproval(false)
  }

  const onDeliveryClose = (updateData = false) => {
    if (updateData) {
      getApplications()
    }
    setShowDelivery(false)
  }

  const exportData = useCallback((recordId, projectName) => {
    const selector = `div.export-table-${recordId}`
    const filename = `Заявка-${recordId}.xlsx`
    exportTableDataToXlsx(selector, projectName, filename)
  }, [])

  const renderApplicationActionMenu = (application) => {
    const { id, current_state, implementation, project, payment } = application
    const menu = (
      <Menu>
        {(current_state === APP_STATUS_PROGRESS || isProject) && implementation && (
          <Menu.Item key="edit" disabled={isForeman} onClick={onImplementationPress(application)} type="primary">
            Редактировать
          </Menu.Item>
        )}
        {current_state === APP_STATUS_PROGRESS && implementation && (
          <Menu.Item key="agree" disabled={isForeman} onClick={onStateChangePress(id, APP_STATUS_AGREE)} type="dashed">
            Согласовать
          </Menu.Item>
        )}
        {current_state === APP_STATUS_ACTIVE && (
          <Menu.Item
            key="progress"
            disabled={isForeman}
            onClick={onStateChangePress(id, APP_STATUS_PROGRESS)}
            type="primary"
          >
            В работу
          </Menu.Item>
        )}
        {(current_state === APP_STATUS_AGREE || current_state === APP_STATUS_PAY) && (
          <Menu.Item
            key="agreemodal"
            disabled={isForeman || isSupplier}
            onClick={() => {
              setSelectedApplication(application)
              setShowApproval(true)
            }}
          >
            Согласовать / Доработка
          </Menu.Item>
        )}
        {current_state === APP_STATUS_AGREE && implementation && (
          <Menu.Item key="more" onClick={onImplementationPress(application)} type="dashed">
            Подробнее
          </Menu.Item>
        )}
        {current_state === APP_STATUS_DRAFT && (
          <Menu.Item key="active" onClick={onStateChangePress(id, APP_STATUS_ACTIVE)} type="primary">
            Активировать
          </Menu.Item>
        )}
        {current_state === APP_STATUS_DRAFT && (
          <Menu.Item key="edit-draft" onClick={onEditPress(application)} type="dashed">
            Редактировать
          </Menu.Item>
        )}
        {current_state === APP_STATUS_DRAFT && (
          <Menu.Item key="remove" onClick={onDeleteChangePress(id)} type="primary" danger>
            Удалить
          </Menu.Item>
        )}
        {current_state === APP_STATUS_ACTIVE && (
          <Menu.Item key="draft" disabled={!isForeman} onClick={onStateChangePress(id, APP_STATUS_DRAFT)} type="dashed">
            Черновик
          </Menu.Item>
        )}
        {[APP_STATUS_PAY, APP_STATUS_DELIVERY, APP_STATUS_DONE].includes(current_state) && (
          <Menu.Item
            key="pay"
            disabled={isForeman}
            onClick={onPaymentShowHandler(id, current_state === APP_STATUS_PAY ? APP_STATUS_DELIVERY : null)}
            type="primary"
          >
            Оплатить
          </Menu.Item>
        )}
        {[APP_STATUS_DELIVERY, APP_STATUS_DONE].includes(current_state) && (
          <Menu.Item
            key="payment-details"
            disabled={isForeman}
            onClick={onPaymentsDetailShowHandler(id)}
            type="primary"
          >
            История оплаты
          </Menu.Item>
        )}
        <Menu.Item
          key="upload"
          onClick={() => {
            setSelectedApplication(application)
            setShowDelivery(true)
          }}
        >
          Загрузить (ТТН)
        </Menu.Item>
        {[APP_STATUS_DELIVERY, APP_STATUS_DONE].includes(current_state) && (
          <Menu.Item
            key="delivery-date"
            disabled={isSupplier}
            onClick={() => {
              setSelectedApplication(application)
              setShowDeliveryDateModal(true)
            }}
            type="primary"
          >
            Дата приемки
          </Menu.Item>
        )}
        {current_state === APP_STATUS_DELIVERY && (
          <Menu.Item
            key="actulAmount"
            onClick={() => {
              setSelectedApplication(application)
              setShowActualAmount(true)
            }}
            type="dashed"
          >
            Поэтапная приемка
          </Menu.Item>
        )}
        {current_state === APP_STATUS_DELIVERY && (
          <Menu.Item key="done" onClick={onStateChangePress(id, APP_STATUS_DONE)} type="dashed">
            Доставлено
          </Menu.Item>
        )}
        {expandedIds.findIndex((item) => item === id) > -1 && (
          <Menu.Item key="download" type="dashed" onClick={() => exportData(id, project?.name)}>
            Выгрузить в XLS
          </Menu.Item>
        )}
        {current_state === APP_STATUS_DONE && (
          <Menu.Item key="refund" onClick={onRefundPress(application)} type="dashed">
            Возврат
          </Menu.Item>
        )}
        {isProject && (
          <Menu.Item
            key="archivemodal"
            danger
            disabled={isForeman || isSupplier}
            onClick={() => showConfirm(id, APP_STATUS_ARCHIVE)}
          >
            Архивировать
          </Menu.Item>
        )}
      </Menu>
    )
    return (
      <Dropdown overlay={menu}>
        <a>
          Действия <DownOutlined />
        </a>
      </Dropdown>
    )
  }

  const mainTableColumns = [
    {
      title: () => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => setExpandedIds(applications.map((item) => item.id))}>Развернуть все</Menu.Item>
            <Menu.Item onClick={() => setExpandedIds([])}>Свернуть все</Menu.Item>
          </Menu>
        )
        return (
          <Dropdown overlay={menu}>
            <a style={{ marginLeft: 5 }}>
              <DownOutlined />
            </a>
          </Dropdown>
        )
      },
      dataIndex: 'expand_control',
      key: 'expand_control',
      width: 40,
      render: (_, record) => {
        const expanded = expandedIds.findIndex((item) => item === record.id)
        if (expanded > -1) {
          return <Button className={styles.expandButton} icon={<MinusOutlined />} size="small" />
        }
        return <Button className={styles.expandButton} icon={<PlusOutlined />} size="small" />
      },
    },
    {
      title: 'Номер',
      dataIndex: 'id',
      key: 'id',
      width: 65,
    },
    {
      title: 'Проект',
      dataIndex: 'project',
      key: 'project',
      render: (project) => project.name,
    },
    {
      title: 'Сумма',
      dataIndex: 'sum',
      key: 'sum',
      width: 100,
      render: (sum, record) => renderApplicationSum(record),
    },
    {
      title: 'Оплата',
      dataIndex: 'payment_type',
      key: 'payment_type',
      width: 120,
      render: (_, record) => renderPaymentInfo(record),
    },
    {
      title: 'Позиции',
      dataIndex: 'implementation',
      key: 'implementation_count',
      width: 80,
      align: 'center',
      render: (implementation) => {
        const countOfImplemented = implementation?.implementation_items.filter((item) => item?.name?.length > 0).length
        return (
          <Tooltip
            title={`${countOfImplemented} из ${implementation?.implementation_items.length} позиций обработаны снабжением`}
          >
            <span>{`${countOfImplemented} / ${implementation?.implementation_items.length}`}</span>
          </Tooltip>
        )
      },
    },
    {
      title: 'Дата заявки',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: (createdAt, record) => format(new Date(createdAt), 'dd-MM-yyyy'),
    },
    {
      title: 'Снабженец',
      dataIndex: 'supplier',
      key: 'supplier',
      render: (supplier, record) =>
        `${record?.implementation?.user?.first_name || ''} ${record?.implementation?.user?.second_name || ''}`,
    },
    {
      title: 'Поставщики',
      dataIndex: 'implementation',
      key: 'provider',
      render: (implementation, record) => {
        const providers = implementation?.implementation_items
          .map((item) => {
            if (item?.provider) {
              return item?.provider
            }
            if (item?.warehouse) {
              return item?.warehouse
            }

            return null
          })
          .filter((item) => item && item.length > 0)
        implementation?.transportation?.forEach((item) => {
          if (item?.provider) {
            providers.push(item?.provider)
          }
        })
        return _.uniq(providers).map((provider) => <div>{provider}</div>)
      },
    },
    {
      title: 'Документы',
      dataIndex: 'documents',
      key: 'documents',
      render: (documents, record) => {
        const files = record?.implementation?.implementation_offers
          ? record?.implementation?.implementation_offers
              .filter((file) => file?.type === 'final')
              .map((file) => ({
                uid: file.id,
                name: file?.offer?.name,
                status: APP_STATUS_DONE,
                url: `${API_URL}${file?.offer?.url}`,
              }))
          : []
        const deliveryFiles = record?.delivery?.delivery_files
          ? record?.delivery?.delivery_files
              .filter((file) => file?.type === 'final')
              .map((file) => ({
                uid: file.id,
                name: file?.file?.name,
                status: APP_STATUS_DONE,
                url: `${API_URL}${file?.file?.url}`,
              }))
          : []
        return (
          <div>
            <div>
              {files && files.length > 0 && (
                <div>
                  <span className={styles.itemLabel}>Счета: </span>
                  <Button
                    style={{ padding: 0 }}
                    type="link"
                    onClick={(event) => {
                      event.stopPropagation()
                      onFilesHandler(files)
                    }}
                  >
                    посмотреть
                  </Button>
                </div>
              )}
            </div>
            <div>
              {deliveryFiles && deliveryFiles.length > 0 && (
                <div>
                  <span className={styles.itemLabel}>ТТН: </span>
                  <Button
                    style={{ padding: 0 }}
                    type="link"
                    onClick={(event) => {
                      event.stopPropagation()
                      onFilesHandler(deliveryFiles)
                    }}
                  >
                    посмотреть
                  </Button>
                </div>
              )}
            </div>
          </div>
        )
      },
    },
    {
      title: 'Реквизиты документов',
      dataIndex: 'document_numbers',
      key: 'document_numbers',
      render: (documents, record) => {
        const files = record?.implementation?.implementation_offers
          ? record?.implementation?.implementation_offers
              .filter((file) => file.type === 'final' && file.number)
              .map((file) => ({
                uid: file.id,
                name: file?.offer?.name,
                status: APP_STATUS_DONE,
                number: file.number,
                date: file.date,
                url: `${API_URL}${file?.offer?.url}`,
              }))
          : []
        return (
          <div>
            {files.map((file) => {
              return (
                <div>
                  <a href={file.url} target="_blank" rel="noreferrer">
                    № {file.number || '-'} от {formatDate(file?.date)}
                  </a>
                </div>
              )
            })}
          </div>
        )
      },
    },
    {
      title: 'Дата приемки',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 120,
      render: (delivery_date, record) => (delivery_date ? format(new Date(delivery_date), 'dd-MM-yyyy') : null),
    },
    {
      title: 'Статус',
      dataIndex: 'current_state',
      key: 'current_state',
      render: (current_state) => statuses.find((item) => item.value === current_state)?.label,
    },
    {
      title: () => {
        return (
          <div className={styles.settingsContainer}>
            <span>Действия</span>
            <Button type="link" icon={<SettingOutlined />} onClick={() => setTableSettingsVisible(true)} />
          </div>
        )
      },
      key: 'action',
      align: 'center',
      render: (text, record) => renderApplicationActionMenu(record),
    },
  ]

  const expandedRowRender = (applicationRecord) => {
    return (
      <ImplementationItemsTable
        tableId={applicationRecord.id}
        storedData={storedData}
        applicationRecord={applicationRecord}
        filter={filter}
      />
    )
  }

  return (
    <Spin spinning={isLoading}>
      <ApplicationFilter filter={filter} onSearch={onSearchFilter} />
      <Table
        rowKey="id"
        expandable={{
          showExpandColumn: false,
          expandedRowRender: expandedRowRender,
          expandRowByClick: true,
          onExpand: (expanded, record) => {
            if (expanded) {
              const newExpandedIds = [...expandedIds]
              newExpandedIds.push(record.id)
              setExpandedIds(newExpandedIds)
            } else {
              const newExpandedIds = expandedIds.filter((item) => item !== record.id)
              setExpandedIds(newExpandedIds)
            }
          },
          expandedRowKeys: expandedIds,
        }}
        pagination={tablePagination}
        size="small"
        columns={mainTableColumns}
        dataSource={applications}
        tableLayout="fixed"
        rowClassName={({ isRejected }) => (isRejected ? 'rejected-row' : '')}
      />
      <AddApplicationPaymentModal
        showModal={showPaymentModal}
        application={selectedApplicationId}
        onFinish={onPaymentApplyHandler}
        onCancel={onPaymentCloseHandler}
      />
      <ApplicationPaymentsDetailModal
        showModal={showPaymentsDetail}
        application={selectedApplicationId}
        onCancel={onPaymentsDetailCloseHandler}
      />
      <Modal
        title="Документы"
        visible={showFileList}
        onCancel={() => setShowFileList(false)}
        footer={[
          <Button key="back" onClick={() => setShowFileList(false)}>
            Закрыть
          </Button>,
        ]}
      >
        <Upload
          fileList={fileList}
          disabled
          showUploadList={{
            showRemoveIcon: false,
          }}
        />
      </Modal>
      <ApproveModal
        application={selectedApplication}
        showModal={showApproval}
        onChancel={onApprovalClose}
        onAgree={onStateChangePress}
        onReject={onChangeState}
        isLoading={isLoading}
        isOnlyReject={selectedApplication?.current_state !== APP_STATUS_AGREE}
      />
      <DeliveryFilesModal application={selectedApplication} showModal={showDelivery} onChancel={onDeliveryClose} />
      <ColumnsSettings
        data={storedData}
        showModal={isTableSettingsVisible}
        onChancel={() => setTableSettingsVisible(false)}
        onSave={(data) => setStoredData(data)}
      />
      <ActualAmountModal
        application={selectedApplication}
        showModal={showActualAmount}
        onChancel={() => setShowActualAmount(false)}
        onUpdate={() => getApplications()}
      />
      <RefundModal
        showModal={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        onSuccess={() => getApplications()}
      />
      <DeliveryDateModal
        showModal={showDeliveryDateModal}
        application={selectedApplication}
        onSuccess={() => getApplications()}
        onChancel={() => setShowDeliveryDateModal(false)}
      />
    </Spin>
  )
}

export default ApplicationsTable
